<template>
  <div class="merge-management-simple">
    <!-- 简约页面头部 -->
    <div class="simple-header">
      <div class="header-left">
        <h1>归并管理</h1>
        <span class="header-desc">票据与银行回单的智能归并处理</span>
      </div>
      <div class="header-stats">
        <span class="stat-item">票据: {{ totalUnmergedDocuments }}</span>
        <span class="stat-item">银行回单: {{ totalUnmergedReceipts }}</span>
        <span class="stat-item">归并组: {{ totalMergeGroups }}</span>
      </div>
    </div>

    <!-- 简约标签导航 -->
    <div class="simple-tabs">
      <div 
        class="tab-btn" 
        :class="{ active: activeTab === 'document' }"
        @click="activeTab = 'document'"
      >
        📄 票据归并 <span class="tab-count">({{ totalUnmergedDocuments }})</span>
      </div>
      <div 
        class="tab-btn" 
        :class="{ active: activeTab === 'receipt' }"
        @click="activeTab = 'receipt'"
      >
        🧾 银行回单归并 <span class="tab-count">({{ totalUnmergedReceipts }})</span>
      </div>
      <div 
        class="tab-btn" 
        :class="{ active: activeTab === 'group' }"
        @click="activeTab = 'group'"
      >
        📁 归并组管理 <span class="tab-count">({{ documentGroups.length + receiptGroups.length }})</span>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 票据归并 -->
        <div v-if="activeTab === 'document'" class="merge-section">
          <!-- 简约模式选择 -->
          <div class="mode-tabs">
            <button 
              class="mode-btn" 
              :class="{ active: documentSubTab === 'manual' }"
              @click="documentSubTab = 'manual'"
            >
              ✋ 手动归并
            </button>
            <button 
              class="mode-btn" 
              :class="{ active: documentSubTab === 'auto' }"
              @click="documentSubTab = 'auto'"
            >
              ⚙️ 规则归并
            </button>
            <button 
              class="mode-btn" 
              :class="{ active: documentSubTab === 'ai' }"
              @click="documentSubTab = 'ai'"
            >
              🤖 AI归并
            </button>
          </div>

          <!-- 手动归并模式 -->
          <div v-if="documentSubTab === 'manual'" class="manual-merge">
            <div class="merge-controls">
              <div class="control-group">
                <label>归并组名称:</label>
                <input
                  v-model="manualDocumentGroupName"
                  placeholder="请输入归并组名称"
                  class="input-field"
                />
              </div>
              <div class="control-group">
                <span class="selected-info">
                  已选择 {{ selectedDocuments.length }} 项，总金额 ¥{{ calculateSelectedAmount() }}
                </span>
              </div>
              <div class="control-group">
                <button 
                  class="action-btn primary"
                  @click="previewManualMerge"
                  :disabled="selectedDocuments.length < 2"
                >
                  预览归并
                </button>
                <button 
                  class="action-btn success"
                  @click="executeManualDocumentMerge"
                  :disabled="!canExecuteDocumentMerge"
                >
                  执行归并 ({{ selectedDocuments.length }}项)
                </button>
              </div>
            </div>
            
            <div class="document-table">
              <div class="table-header">
                <div class="header-left">
                  <h3>未归并票据 ({{ filteredUnmergedDocuments.length }})</h3>
                </div>
                <div class="header-right">
                  <input 
                    v-model="documentSearchKeyword"
                    placeholder="搜索票据..." 
                    class="search-input"
                  />
                  <button 
                    class="action-btn small"
                    @click="toggleSelectAllDocuments"
                  >
                    {{ selectAllDocuments ? '取消全选' : '全选' }}
                  </button>
                  <button 
                    class="action-btn small"
                    @click="clearDocumentSelection"
                  >
                    清空选择
                  </button>
                </div>
              </div>
              
              <table class="simple-table">
                <thead>
                  <tr>
                    <th width="40">选择</th>
                    <th width="120">票据编号</th>
                    <th width="100">日期</th>
                    <th width="100">金额</th>
                    <th>摘要</th>
                    <th width="120">开票方</th>
                    <th width="80">类型</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="doc in filteredUnmergedDocuments" 
                    :key="doc.id"
                    :class="{ selected: selectedDocuments.includes(doc.id) }"
                    @click="toggleDocumentSelection(doc.id)"
                  >
                    <td>
                      <input 
                        type="checkbox" 
                        :checked="selectedDocuments.includes(doc.id)"
                        @click.stop
                        @change="toggleDocumentSelection(doc.id)"
                      />
                    </td>
                    <td>{{ doc.billNo }}</td>
                    <td>{{ formatDate(doc.billDate) }}</td>
                    <td class="amount">¥{{ formatAmount(doc.amount) }}</td>
                    <td>{{ doc.summary }}</td>
                    <td>{{ doc.issuer }}</td>
                    <td>
                      <span class="type-tag">{{ doc.type }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 规则归并模式 -->
          <div v-if="documentSubTab === 'auto'" class="auto-merge">
            <div class="rule-controls">
              <div class="control-group">
                <label>选择归并规则:</label>
                <select v-model="selectedRuleId" class="rule-select">
                  <option value="">请选择规则</option>
                  <option 
                    v-for="rule in mergeRules" 
                    :key="rule.id" 
                    :value="rule.id"
                  >
                    {{ rule.ruleName }}
                  </option>
                </select>
              </div>
              <div class="control-group">
                <label>
                  <input 
                    type="checkbox" 
                    v-model="previewMode"
                  />
                  预览模式（不直接执行）
                </label>
              </div>
              <div class="control-group">
                <button 
                  class="action-btn primary"
                  @click="executeRuleMerge"
                  :disabled="!selectedRuleId"
                >
                  {{ previewMode ? '预览规则归并' : '执行规则归并' }}
                </button>
                <button 
                  class="action-btn secondary"
                  @click="goToRuleManagement"
                >
                  管理规则
                </button>
              </div>
            </div>

            <div v-if="rulePreviewResults" class="rule-results">
              <h3>规则归并结果预览</h3>
              <div class="result-summary">
                <span>预计生成 {{ rulePreviewResults.groups.length }} 个归并组</span>
                <span>涉及 {{ rulePreviewResults.totalItems }} 个票据</span>
              </div>
              <div class="result-list">
                <div 
                  v-for="(group, index) in rulePreviewResults.groups" 
                  :key="index"
                  class="result-item"
                >
                  <div class="result-header">
                    <span class="group-name">{{ group.groupName }}</span>
                    <span class="item-count">{{ group.items.length }} 项</span>
                    <button 
                      class="action-btn small success"
                      @click="confirmRuleGroup(group)"
                    >
                      确认归并
                    </button>
                  </div>
                  <div class="result-content">
                    <span class="rule-reason">{{ group.reason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI智能归并模式 -->
          <div v-if="documentSubTab === 'ai'" class="ai-merge">
            <div class="ai-controls">
              <div class="ai-info">
                <span>🤖 AI将分析 {{ aiUnmergedDocuments.length }} 个票据，基于开票方、日期、金额等因素进行智能匹配</span>
              </div>
              <div class="ai-actions">
                <button 
                  class="action-btn primary"
                  @click="analyzeDocumentMergeWithAi"
                  :disabled="aiUnmergedDocuments.length === 0 || aiAnalyzing"
                >
                  {{ aiAnalyzing ? '🔄 AI分析中...' : '🚀 开始AI分析' }}
                </button>
                <span v-if="aiDocumentSuggestions" class="ai-result">
                  AI建议了 {{ aiDocumentSuggestions.groups.length }} 个归并组
                </span>
              </div>
            </div>
            
            <div v-if="aiDocumentSuggestions" class="ai-suggestions">
              <div class="suggestions-header">
                <h3>AI归并建议</h3>
                <div class="suggestions-actions">
                  <button 
                    class="action-btn small"
                    @click="selectAllAiDocumentGroups"
                  >
                    全选
                  </button>
                  <button 
                    class="action-btn small"
                    @click="clearAiDocumentGroupSelection"
                  >
                    清空
                  </button>
                  <button 
                    class="action-btn success"
                    @click="executeSelectedAiDocumentGroups"
                    :disabled="selectedAiDocumentGroups.length === 0"
                  >
                    执行选中 ({{ selectedAiDocumentGroups.length }})
                  </button>
                </div>
              </div>
              
              <div class="suggestion-list">
                <div 
                  v-for="(group, index) in aiDocumentSuggestions.groups" 
                  :key="index"
                  class="suggestion-item"
                  :class="{ selected: selectedAiDocumentGroups.includes(index) }"
                >
                  <div class="suggestion-header">
                    <input 
                      type="checkbox" 
                      :checked="selectedAiDocumentGroups.includes(index)"
                      @change="toggleAiDocumentGroupSelection(index)"
                    />
                    <span class="group-name">{{ group.groupName }}</span>
                    <span class="confidence">置信度: {{ calculateConfidence(group) }}%</span>
                    <button 
                      class="action-btn small"
                      @click="executeSingleAiDocumentGroup(group, index)"
                    >
                      单独执行
                    </button>
                  </div>
                  <div class="suggestion-content">
                    <span class="item-count">{{ group.billIds.length }} 个票据</span>
                    <span class="reason">{{ group.reason }}</span>
                  </div>
                  <div class="suggestion-items">
                    <span 
                      v-for="billId in group.billIds" 
                      :key="billId"
                      class="item-tag"
                    >
                      {{ getDocumentInfo(billId) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 银行回单归并 -->
        <div v-if="activeTab === 'receipt'" class="merge-section">
          <!-- 简约模式选择 -->
          <div class="mode-tabs">
            <button
              class="mode-btn"
              :class="{ active: receiptSubTab === 'manual' }"
              @click="receiptSubTab = 'manual'"
            >
              ✋ 手动归并
            </button>
            <button
              class="mode-btn"
              :class="{ active: receiptSubTab === 'auto' }"
              @click="receiptSubTab = 'auto'"
            >
              ⚙️ 规则归并
            </button>
            <button
              class="mode-btn"
              :class="{ active: receiptSubTab === 'ai' }"
              @click="receiptSubTab = 'ai'"
            >
              🤖 AI归并
            </button>
          </div>

          <!-- 手动归并模式 -->
          <div v-if="receiptSubTab === 'manual'" class="manual-merge">
            <div class="merge-controls">
              <div class="control-group">
                <label>归并组名称:</label>
                <input
                  v-model="manualReceiptGroupName"
                  placeholder="请输入归并组名称"
                  class="input-field"
                />
              </div>
              <div class="control-group">
                <span class="selected-info">
                  已选择 {{ selectedReceipts.length }} 项，总金额 ¥{{ calculateSelectedReceiptAmount() }}
                </span>
              </div>
              <div class="control-group">
                <button
                  class="action-btn primary"
                  @click="previewManualReceiptMerge"
                  :disabled="selectedReceipts.length < 2"
                >
                  预览归并
                </button>
                <button
                  class="action-btn success"
                  @click="executeManualReceiptMerge"
                  :disabled="!canExecuteReceiptMerge"
                >
                  执行归并 ({{ selectedReceipts.length }}项)
                </button>
              </div>
            </div>

            <div class="document-table">
              <div class="table-header">
                <div class="header-left">
                  <h3>未归并银行回单 ({{ filteredUnmergedReceipts.length }})</h3>
                </div>
                <div class="header-right">
                  <input
                    v-model="receiptSearchKeyword"
                    placeholder="搜索银行回单..."
                    class="search-input"
                  />
                  <button
                    class="action-btn small"
                    @click="toggleSelectAllReceipts"
                  >
                    {{ selectAllReceipts ? '取消全选' : '全选' }}
                  </button>
                  <button
                    class="action-btn small"
                    @click="clearReceiptSelection"
                  >
                    清空选择
                  </button>
                </div>
              </div>

              <table class="simple-table">
                <thead>
                  <tr>
                    <th width="40">选择</th>
                    <th width="140">单据编号</th>
                    <th width="120">转账日期</th>
                    <th width="80">类型</th>
                    <th width="140">金额</th>
                    <th width="180">收款人/付款人</th>
                    <th>摘要</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="receipt in filteredUnmergedReceipts"
                    :key="receipt.id"
                    :class="{ selected: selectedReceipts.includes(receipt.id) }"
                    @click="toggleReceiptSelection(receipt.id)"
                  >
                    <td>
                      <input
                        type="checkbox"
                        :checked="selectedReceipts.includes(receipt.id)"
                        @click.stop
                        @change="toggleReceiptSelection(receipt.id)"
                      />
                    </td>
                    <td>
                      <div class="cell-content">
                        <div class="primary-text">{{ receipt.receiptsNo || receipt.receiptNo }}</div>
                        <div class="secondary-text" v-if="receipt.serialNumber">流水号: {{ receipt.serialNumber }}</div>
                      </div>
                    </td>
                    <td>
                      <div class="cell-content">
                        <div class="primary-text">{{ formatDate(receipt.transferDate || receipt.receiptDate) }}</div>
                        <div class="secondary-text" v-if="receipt.receiptDate && receipt.transferDate && receipt.receiptDate !== receipt.transferDate">
                          单据: {{ formatDate(receipt.receiptDate) }}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="type-tag" :class="getTypeBadgeClass(receipt.type)">{{ receipt.type }}</span>
                    </td>
                    <td>
                      <div class="cell-content">
                        <div class="amount-text">¥{{ formatAmount(receipt.amount) }}</div>
                        <div class="secondary-text" v-if="receipt.amountInWords">{{ receipt.amountInWords }}</div>
                      </div>
                    </td>
                    <td>
                      <div class="cell-content">
                        <div class="primary-text" :title="getCounterpartyDisplay(receipt)">
                          {{ getCounterpartyDisplay(receipt) || '-' }}
                        </div>
                        <div class="secondary-text" v-if="getCounterpartyAccount(receipt)">
                          {{ getCounterpartyAccount(receipt) }}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="cell-content">
                        <div class="primary-text" :title="receipt.summary">{{ receipt.summary || '-' }}</div>
                        <div class="secondary-text" v-if="receipt.transactionInstitution">
                          {{ receipt.transactionInstitution }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- AI归并模式 -->
          <div v-if="receiptSubTab === 'ai'" class="ai-merge">
            <div class="ai-controls">
              <div class="ai-info">
                <span>🤖 AI将分析 {{ aiUnmergedReceipts.length }} 个银行回单，基于银行、日期、金额等因素进行智能匹配</span>
              </div>
              <div class="ai-actions">
                <button
                  class="action-btn primary"
                  @click="analyzeReceiptMergeWithAi"
                  :disabled="aiUnmergedReceipts.length === 0 || aiReceiptAnalyzing"
                >
                  {{ aiReceiptAnalyzing ? '🔄 AI分析中...' : '🚀 开始AI分析' }}
                </button>
                <span v-if="aiReceiptSuggestions" class="ai-result">
                  AI建议了 {{ aiReceiptSuggestions.groups.length }} 个归并组
                </span>
              </div>
            </div>

            <div v-if="aiReceiptSuggestions" class="ai-suggestions">
              <div class="suggestions-header">
                <h3>AI归并建议</h3>
                <div class="suggestions-actions">
                  <button
                    class="action-btn small"
                    @click="selectAllAiReceiptGroups"
                  >
                    全选
                  </button>
                  <button
                    class="action-btn small"
                    @click="clearAiReceiptGroupSelection"
                  >
                    清空
                  </button>
                  <button
                    class="action-btn success"
                    @click="executeSelectedAiReceiptGroups"
                    :disabled="selectedAiReceiptGroups.length === 0"
                  >
                    执行选中 ({{ selectedAiReceiptGroups.length }})
                  </button>
                </div>
              </div>

              <div class="suggestion-list">
                <div
                  v-for="(group, index) in aiReceiptSuggestions.groups"
                  :key="index"
                  class="suggestion-item"
                  :class="{ selected: selectedAiReceiptGroups.includes(index) }"
                >
                  <div class="suggestion-header">
                    <input
                      type="checkbox"
                      :checked="selectedAiReceiptGroups.includes(index)"
                      @change="toggleAiReceiptGroupSelection(index)"
                    />
                    <span class="group-name">{{ group.groupName }}</span>
                    <span class="confidence">置信度: {{ calculateConfidence(group) }}%</span>
                    <button
                      class="action-btn small"
                      @click="executeSingleAiReceiptGroup(group, index)"
                    >
                      单独执行
                    </button>
                  </div>
                  <div class="suggestion-content">
                    <span class="item-count">{{ group.receiptIds.length }} 个银行回单</span>
                    <span class="reason">{{ group.reason }}</span>
                  </div>
                  <div class="suggestion-items">
                    <span
                      v-for="receiptId in group.receiptIds"
                      :key="receiptId"
                      class="item-tag"
                    >
                      {{ getReceiptInfo(receiptId) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 归并组管理 -->
        <div v-if="activeTab === 'group'" class="merge-section">
          <!-- 组类型选择 -->
          <div class="mode-tabs">
            <button
              class="mode-btn"
              :class="{ active: groupSubTab === 'document' }"
              @click="groupSubTab = 'document'"
            >
              📄 票据归并组 ({{ documentGroups.length }})
            </button>
            <button
              class="mode-btn"
              :class="{ active: groupSubTab === 'receipt' }"
              @click="groupSubTab = 'receipt'"
            >
              🧾 银行回单归并组 ({{ receiptGroups.length }})
            </button>
          </div>

          <!-- 票据归并组列表 -->
          <div v-if="groupSubTab === 'document'" class="group-management">
            <div class="table-header">
              <div class="header-left">
                <h3>票据归并组 ({{ documentGroups.length }})</h3>
              </div>
              <div class="header-right">
                <input
                  v-model="groupSearchKeyword"
                  placeholder="搜索归并组..."
                  class="search-input"
                />
                <button
                  class="action-btn primary"
                  @click="refreshDocumentGroups"
                >
                  刷新
                </button>
              </div>
            </div>

            <table class="simple-table">
              <thead>
                <tr>
                  <th width="200">组名称</th>
                  <th width="100">票据数量</th>
                  <th width="120">创建时间</th>
                  <th width="100">状态</th>
                  <th width="150">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="group in filteredDocumentGroups"
                  :key="group.groupId"
                >
                  <td>{{ group.groupName }}</td>
                  <td>{{ group.itemCount || 0 }}</td>
                  <td>{{ formatDate(group.createdAt) }}</td>
                  <td>
                    <span class="status-tag" :class="group.status">
                      {{ getStatusText(group.status) }}
                    </span>
                  </td>
                  <td>
                    <button
                      class="action-btn small"
                      @click="viewDocumentGroup(group)"
                    >
                      查看
                    </button>
                    <button
                      class="action-btn small"
                      style="background: #dc3545; color: white;"
                      @click="dissolveDocumentGroup(group)"
                    >
                      解散
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 银行回单归并组列表 -->
          <div v-if="groupSubTab === 'receipt'" class="group-management">
            <div class="table-header">
              <div class="header-left">
                <h3>银行回单归并组 ({{ receiptGroups.length }})</h3>
              </div>
              <div class="header-right">
                <input
                  v-model="groupSearchKeyword"
                  placeholder="搜索归并组..."
                  class="search-input"
                />
                <button
                  class="action-btn primary"
                  @click="refreshReceiptGroups"
                >
                  刷新
                </button>
              </div>
            </div>

            <table class="simple-table">
              <thead>
                <tr>
                  <th width="200">组名称</th>
                  <th width="100">银行回单数量</th>
                  <th width="120">创建时间</th>
                  <th width="100">状态</th>
                  <th width="150">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="group in filteredReceiptGroups"
                  :key="group.groupId"
                >
                  <td>{{ group.groupName }}</td>
                  <td>{{ group.itemCount || 0 }}</td>
                  <td>{{ formatDate(group.createdAt) }}</td>
                  <td>
                    <span class="status-tag" :class="group.status">
                      {{ getStatusText(group.status) }}
                    </span>
                  </td>
                  <td>
                    <button
                      class="action-btn small"
                      @click="viewReceiptGroup(group)"
                    >
                      查看
                    </button>
                    <button
                      class="action-btn small"
                      style="background: #dc3545; color: white;"
                      @click="dissolveReceiptGroup(group)"
                    >
                      解散
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 归并组详情浮窗 -->
    <MergeGroupDetailModal
      :visible="groupDetailVisible"
      :groupId="currentGroupId"
      :groupType="currentGroupType"
      @close="closeGroupDetail"
      @updated="handleGroupUpdated"
      @dissolved="handleGroupDissolved"
    />
  </div>
</template>

<script>
import '../../styles/merge-simple.css'
import { mergeManagementApi } from '../../api/merge-management.js'
import MergeGroupDetailModal from '../../components/MergeGroupDetailModal.vue'

export default {
  name: 'MergeManagement',
  components: {
    MergeGroupDetailModal
  },
  data() {
    return {
      activeTab: 'document',
      documentSubTab: 'manual',
      receiptSubTab: 'manual',
      groupSubTab: 'document',

      // 统计数据
      totalUnmergedDocuments: 0,
      totalUnmergedReceipts: 0,
      totalMergeGroups: 0,
      totalDocumentGroups: 0,
      totalReceiptGroups: 0,

      // 票据手动归并
      unmergedDocuments: [],
      selectedDocuments: [],
      manualDocumentGroupName: '',
      documentSearchKeyword: '',
      selectAllDocuments: false,

      // 银行回单手动归并
      unmergedReceipts: [],
      selectedReceipts: [],
      manualReceiptGroupName: '',
      receiptSearchKeyword: '',
      selectAllReceipts: false,

      // 规则归并
      mergeRules: [],
      selectedRuleId: '',
      previewMode: true,
      rulePreviewResults: null,

      // AI归并
      aiUnmergedDocuments: [],
      aiDocumentSuggestions: null,
      aiAnalyzing: false,
      selectedAiDocumentGroups: [],

      // AI银行回单归并
      aiUnmergedReceipts: [],
      aiReceiptSuggestions: null,
      aiReceiptAnalyzing: false,
      selectedAiReceiptGroups: [],

      // 归并组管理
      documentGroups: [],
      receiptGroups: [],
      groupSearchKeyword: '',

      // 归并组详情浮窗
      groupDetailVisible: false,
      currentGroupId: '',
      currentGroupType: 'document' // 'document' 或 'receipt'
    }
  },
  
  computed: {
    canExecuteDocumentMerge() {
      const hasEnoughItems = this.selectedDocuments.length >= 2
      const hasGroupName = this.manualDocumentGroupName && this.manualDocumentGroupName.trim().length > 0
      return hasEnoughItems && hasGroupName
    },

    canExecuteReceiptMerge() {
      const hasEnoughItems = this.selectedReceipts.length >= 2
      const hasGroupName = this.manualReceiptGroupName && this.manualReceiptGroupName.trim().length > 0
      return hasEnoughItems && hasGroupName
    },

    filteredUnmergedDocuments() {
      if (!this.documentSearchKeyword) {
        return this.unmergedDocuments
      }
      const keyword = this.documentSearchKeyword.toLowerCase()
      return this.unmergedDocuments.filter(doc =>
        (doc.billNo && doc.billNo.toLowerCase().includes(keyword)) ||
        (doc.summary && doc.summary.toLowerCase().includes(keyword)) ||
        (doc.issuer && doc.issuer.toLowerCase().includes(keyword)) ||
        (doc.recipient && doc.recipient.toLowerCase().includes(keyword))
      )
    },

    filteredUnmergedReceipts() {
      if (!this.receiptSearchKeyword) {
        return this.unmergedReceipts
      }
      const keyword = this.receiptSearchKeyword.toLowerCase()
      return this.unmergedReceipts.filter(receipt =>
        (receipt.receiptNo && receipt.receiptNo.toLowerCase().includes(keyword)) ||
        (receipt.summary && receipt.summary.toLowerCase().includes(keyword)) ||
        (receipt.bankName && receipt.bankName.toLowerCase().includes(keyword))
      )
    },

    filteredDocumentGroups() {
      if (!this.groupSearchKeyword) {
        return this.documentGroups
      }
      const keyword = this.groupSearchKeyword.toLowerCase()
      return this.documentGroups.filter(group =>
        (group.groupName && group.groupName.toLowerCase().includes(keyword))
      )
    },

    filteredReceiptGroups() {
      if (!this.groupSearchKeyword) {
        return this.receiptGroups
      }
      const keyword = this.groupSearchKeyword.toLowerCase()
      return this.receiptGroups.filter(group =>
        (group.groupName && group.groupName.toLowerCase().includes(keyword))
      )
    }
  },
  
  mounted() {
    // 检查是否从归并组详情页面返回
    const from = this.$route.query.from
    if (from === 'group-detail') {
      this.activeTab = 'group'
    }
    this.loadData()
  },
  
  methods: {
    async loadData() {
      await this.loadUnmergedDocuments()
      await this.loadUnmergedReceipts()
      await this.loadMergeRules()
      await this.loadDocumentGroups()
      await this.loadReceiptGroups()
      await this.loadStatistics()
    },

    async loadUnmergedDocuments() {
      try {
        const result = await mergeManagementApi.getUnmergedDocuments()
        if (result.success) {
          this.unmergedDocuments = result.data || []
          this.aiUnmergedDocuments = [...this.unmergedDocuments]
        } else {
          this.$Message.error(result.message || '加载未归并票据失败')
        }
      } catch (error) {
        console.error('加载未归并票据失败:', error)
        this.$Message.error('加载数据失败')
      }
    },

    async loadUnmergedReceipts() {
      try {
        const result = await mergeManagementApi.getUnmergedReceipts()
        if (result.success) {
          this.unmergedReceipts = result.data || []
          this.aiUnmergedReceipts = [...this.unmergedReceipts]
        } else {
          this.$Message.error(result.message || '加载未归并银行回单失败')
        }
      } catch (error) {
        console.error('加载未归并银行回单失败:', error)
        this.$Message.error('加载银行回单数据失败')
      }
    },

    async loadMergeRules() {
      try {
        const result = await mergeManagementApi.getMergeRules('BILL')
        if (result.success) {
          this.mergeRules = result.data || []
        } else {
          console.warn('加载归并规则失败:', result.message)
        }
      } catch (error) {
        console.error('加载归并规则失败:', error)
      }
    },

    async loadStatistics() {
      try {
        const result = await mergeManagementApi.getStatistics()
        if (result.success) {
          const data = result.data
          // 计算未归并数量 - 使用实际加载的数据而不是API统计
          this.totalUnmergedDocuments = this.unmergedDocuments.length
          this.totalUnmergedReceipts = this.unmergedReceipts.length
          this.totalMergeGroups = data.mergedGroups || 0

          // 分别获取票据归并组和银行回单归并组数量
          this.totalDocumentGroups = data.mergedDocumentGroups || 0
          this.totalReceiptGroups = data.mergedReceiptGroups || 0
        } else {
          console.warn('加载统计数据失败:', result.message)
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
      }
    },

    async loadDocumentGroups() {
      try {
        const result = await mergeManagementApi.getDocumentGroups()
        if (result.success) {
          this.documentGroups = result.data || []
        } else {
          console.warn('加载票据归并组失败:', result.message)
        }
      } catch (error) {
        console.error('加载票据归并组失败:', error)
      }
    },

    async loadReceiptGroups() {
      try {
        const result = await mergeManagementApi.getReceiptGroups()
        if (result.success) {
          this.receiptGroups = result.data || []
        } else {
          console.warn('加载银行回单归并组失败:', result.message)
        }
      } catch (error) {
        console.error('加载银行回单归并组失败:', error)
      }
    },

    // 格式化方法
    formatAmount(amount) {
      if (!amount) return '0.00'
      return parseFloat(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatDate(date) {
      if (!date) return ''
      if (typeof date === 'string') {
        return date
      }
      return new Date(date).toLocaleDateString('zh-CN')
    },

    // 银行回单显示相关方法
    getCounterpartyDisplay(receipt) {
      // 根据类型显示收款人或付款人信息
      if (receipt.type === '收入') {
        // 收入类型显示付款人信息
        return receipt.payerName || receipt.payerAccount || '未知付款人';
      } else {
        // 支出类型显示收款人信息
        return receipt.payeeName || receipt.payeeAccount || '未知收款人';
      }
    },

    getCounterpartyAccount(receipt) {
      // 根据类型显示对应的账号
      if (receipt.type === '收入') {
        return receipt.payerAccount;
      } else if (receipt.type === '支出') {
        return receipt.payeeAccount;
      }
      return receipt.payerAccount || receipt.payeeAccount;
    },

    getTypeBadgeClass(type) {
      switch(type) {
        case '收入':
          return 'type-income';
        case '支出':
          return 'type-expense';
        default:
          return 'type-default';
      }
    },

    // 手动归并相关方法
    toggleDocumentSelection(docId) {
      const index = this.selectedDocuments.indexOf(docId)
      if (index > -1) {
        this.selectedDocuments.splice(index, 1)
      } else {
        this.selectedDocuments.push(docId)
      }
    },

    toggleSelectAllDocuments() {
      if (this.selectAllDocuments) {
        this.selectedDocuments = []
        this.selectAllDocuments = false
      } else {
        this.selectedDocuments = this.filteredUnmergedDocuments.map(doc => doc.id)
        this.selectAllDocuments = true
      }
    },

    clearDocumentSelection() {
      this.selectedDocuments = []
      this.selectAllDocuments = false
    },

    getDocumentById(docId) {
      return this.unmergedDocuments.find(doc => doc.id === docId)
    },

    getDocumentBillNo(docId) {
      const doc = this.getDocumentById(docId)
      return doc ? doc.billNo : ''
    },

    calculateSelectedAmount() {
      return this.selectedDocuments.reduce((total, docId) => {
        const doc = this.getDocumentById(docId)
        return total + (doc ? parseFloat(doc.amount || 0) : 0)
      }, 0).toFixed(2)
    },

    // 银行回单归并相关方法
    toggleReceiptSelection(receiptId) {
      const index = this.selectedReceipts.indexOf(receiptId)
      if (index > -1) {
        this.selectedReceipts.splice(index, 1)
      } else {
        this.selectedReceipts.push(receiptId)
      }
    },

    toggleSelectAllReceipts() {
      if (this.selectAllReceipts) {
        this.selectedReceipts = []
        this.selectAllReceipts = false
      } else {
        this.selectedReceipts = this.filteredUnmergedReceipts.map(receipt => receipt.id)
        this.selectAllReceipts = true
      }
    },

    clearReceiptSelection() {
      this.selectedReceipts = []
      this.selectAllReceipts = false
    },

    getReceiptById(receiptId) {
      return this.unmergedReceipts.find(receipt => receipt.id === receiptId)
    },

    calculateSelectedReceiptAmount() {
      return this.selectedReceipts.reduce((total, receiptId) => {
        const receipt = this.getReceiptById(receiptId)
        return total + (receipt ? parseFloat(receipt.amount || 0) : 0)
      }, 0).toFixed(2)
    },

    async previewManualReceiptMerge() {
      if (this.selectedReceipts.length < 2) {
        this.$Message.warning('请至少选择2个银行回单进行归并')
        return
      }

      const selectedReceipts = this.selectedReceipts.map(id => this.getReceiptById(id))
      const totalAmount = this.calculateSelectedReceiptAmount()

      const content = `归并组名称: ${this.manualReceiptGroupName}\n银行回单数量: ${this.selectedReceipts.length} 个\n总金额: ¥${totalAmount}\n\n银行回单列表:\n${selectedReceipts.map(receipt => `${receipt.receiptNo} - ¥${this.formatAmount(receipt.amount)}`).join('\n')}`

      this.$Confirm(content, '银行回单归并预览').then(() => {
        this.executeManualReceiptMerge()
      })
    },

    async executeManualReceiptMerge() {
      if (!this.canExecuteReceiptMerge) {
        this.$Message.warning('请选择至少2个银行回单并输入归并组名称')
        return
      }

      try {
        const result = await mergeManagementApi.createReceiptGroup({
          groupName: this.manualReceiptGroupName.trim(),
          receiptIds: this.selectedReceipts
        })

        if (result.success) {
          this.$Message.success('银行回单归并成功！')
          this.selectedReceipts = []
          this.manualReceiptGroupName = ''
          this.selectAllReceipts = false
          await this.loadData()
        } else {
          this.$Message.error(result.message || '银行回单归并失败')
        }
      } catch (error) {
        console.error('执行银行回单归并失败:', error)
        this.$Message.error('银行回单归并操作失败')
      }
    },

    async previewManualMerge() {
      if (this.selectedDocuments.length < 2) {
        this.$Message.warning('请至少选择2个票据进行归并')
        return
      }

      const selectedDocs = this.selectedDocuments.map(id => this.getDocumentById(id))
      const totalAmount = this.calculateSelectedAmount()

      const content = `归并组名称: ${this.manualDocumentGroupName}\n票据数量: ${this.selectedDocuments.length} 个\n总金额: ¥${totalAmount}\n\n票据列表:\n${selectedDocs.map(doc => `${doc.billNo} - ¥${this.formatAmount(doc.amount)}`).join('\n')}`

      this.$Confirm(content, '归并预览').then(() => {
        this.executeManualDocumentMerge()
      })
    },

    async executeManualDocumentMerge() {
      if (!this.canExecuteDocumentMerge) {
        this.$Message.warning('请选择至少2个票据并输入归并组名称')
        return
      }

      try {
        const result = await mergeManagementApi.createDocumentGroup({
          groupName: this.manualDocumentGroupName.trim(),
          documentIds: this.selectedDocuments
        })

        if (result.success) {
          this.$Message.success('票据归并成功！')
          this.selectedDocuments = []
          this.manualDocumentGroupName = ''
          this.selectAllDocuments = false
          await this.loadData()
        } else {
          this.$Message.error(result.message || '归并失败')
        }
      } catch (error) {
        console.error('执行归并失败:', error)
        this.$Message.error('归并操作失败')
      }
    },

    // 规则归并相关方法
    async executeRuleMerge() {
      if (!this.selectedRuleId) {
        this.$Message.warning('请选择归并规则')
        return
      }

      try {
        let result
        if (this.previewMode) {
          result = await mergeManagementApi.previewDocumentRuleMerge(this.selectedRuleId)
        } else {
          result = await mergeManagementApi.executeDocumentRuleMerge({
            ruleId: this.selectedRuleId,
            previewOnly: false
          })
        }

        if (result.success) {
          if (this.previewMode) {
            this.rulePreviewResults = result.data
            const groupCount = result.data && result.data.groups ? result.data.groups.length : 0
            this.$Message.info(`预览完成，预计生成 ${groupCount} 个归并组`)
          } else {
            const createdCount = result.data && result.data.groupsCreated ? result.data.groupsCreated : 0
            this.$Message.success(`规则归并完成，生成了 ${createdCount} 个归并组`)
            await this.loadData()
          }
        } else {
          this.$Message.error(result.message || '规则归并失败')
        }
      } catch (error) {
        console.error('执行规则归并失败:', error)
        this.$Message.error('规则归并操作失败')
      }
    },

    async confirmRuleGroup(group) {
      try {
        const result = await mergeManagementApi.createDocumentGroup({
          groupName: group.groupName,
          documentIds: group.items.map(item => item.id)
        })

        if (result.success) {
          this.$Message.success('归并组创建成功！')
          await this.loadData()
          // 从预览结果中移除已确认的组
          const groupIndex = this.rulePreviewResults.groups.indexOf(group)
          if (groupIndex > -1) {
            this.rulePreviewResults.groups.splice(groupIndex, 1)
          }
        } else {
          this.$Message.error(result.message || '创建归并组失败')
        }
      } catch (error) {
        console.error('确认规则组失败:', error)
        this.$Message.error('操作失败')
      }
    },

    goToRuleManagement() {
      this.$router.push('/merge-rule-management')
    },

    // AI归并相关方法
    async analyzeDocumentMergeWithAi() {
      if (this.aiUnmergedDocuments.length === 0) {
        this.$Message.warning('没有可分析的票据')
        return
      }

      this.aiAnalyzing = true
      try {
        // 提取票据ID列表
        const billIds = this.aiUnmergedDocuments.map(doc => doc.id)
        const result = await mergeManagementApi.analyzeDocumentMerge(billIds)

        if (result.success) {
          this.aiDocumentSuggestions = result.data
          this.selectedAiDocumentGroups = []
          const groupCount = result.data && result.data.groups ? result.data.groups.length : 0
          this.$Message.success(`AI分析完成，建议了 ${groupCount} 个归并组`)
        } else {
          this.$Message.error(result.message || 'AI分析失败')
        }
      } catch (error) {
        console.error('AI分析失败:', error)
        this.$Message.error('AI分析操作失败')
      } finally {
        this.aiAnalyzing = false
      }
    },

    calculateConfidence(group) {
      // 简单的置信度计算逻辑
      let confidence = 70
      if (group.billIds && group.billIds.length > 3) confidence += 10
      if (group.reason && group.reason.includes('相同')) confidence += 15
      if (group.reason && group.reason.includes('金额')) confidence += 5
      return Math.min(confidence, 95)
    },

    getDocumentInfo(billId) {
      const doc = this.aiUnmergedDocuments.find(d => d.id === billId)
      if (!doc) return ''
      return `${doc.billNo || ''} ¥${this.formatAmount(doc.amount)}`
    },

    toggleAiDocumentGroupSelection(index) {
      const selectedIndex = this.selectedAiDocumentGroups.indexOf(index)
      if (selectedIndex > -1) {
        this.selectedAiDocumentGroups.splice(selectedIndex, 1)
      } else {
        this.selectedAiDocumentGroups.push(index)
      }
    },

    selectAllAiDocumentGroups() {
      if (this.aiDocumentSuggestions && this.aiDocumentSuggestions.groups) {
        this.selectedAiDocumentGroups = this.aiDocumentSuggestions.groups.map((_, index) => index)
      }
    },

    clearAiDocumentGroupSelection() {
      this.selectedAiDocumentGroups = []
    },

    async executeSingleAiDocumentGroup(group, index) {
      try {
        const result = await mergeManagementApi.executeSingleDocumentAiMerge({
          billIds: group.billIds,
          groupName: group.groupName,
          reason: group.reason
        })

        if (result.success) {
          this.$Message.success('AI归并组创建成功！')
          await this.loadData()
          // 从建议中移除已执行的组
          this.aiDocumentSuggestions.groups.splice(index, 1)
          // 更新选中状态
          this.selectedAiDocumentGroups = this.selectedAiDocumentGroups
            .filter(i => i !== index)
            .map(i => i > index ? i - 1 : i)
        } else {
          this.$Message.error(result.message || '创建AI归并组失败')
        }
      } catch (error) {
        console.error('执行AI归并失败:', error)
        this.$Message.error('AI归并操作失败')
      }
    },

    async executeSelectedAiDocumentGroups() {
      if (this.selectedAiDocumentGroups.length === 0) {
        this.$Message.warning('请选择要执行的归并组')
        return
      }

      const selectedGroups = this.selectedAiDocumentGroups.map(index =>
        this.aiDocumentSuggestions.groups[index]
      )

      try {
        const result = await mergeManagementApi.executeSelectedDocumentAiMerge(
          selectedGroups.map(group => ({
            billIds: group.billIds,
            groupName: group.groupName,
            reason: group.reason
          }))
        )

        if (result.success) {
          const createdCount = result.data && result.data.createdCount ? result.data.createdCount : selectedGroups.length
          this.$Message.success(`批量创建成功，共创建了 ${createdCount} 个归并组`)
          await this.loadData()
          // 清空AI建议
          this.aiDocumentSuggestions = null
          this.selectedAiDocumentGroups = []
        } else {
          this.$Message.error(result.message || '批量创建失败')
        }
      } catch (error) {
        console.error('批量执行AI归并失败:', error)
        this.$Message.error('批量操作失败')
      }
    },

    // AI银行回单归并相关方法
    async analyzeReceiptMergeWithAi() {
      if (this.aiUnmergedReceipts.length === 0) {
        this.$Message.warning('没有可分析的银行回单')
        return
      }

      this.aiReceiptAnalyzing = true
      try {
        // 提取银行回单ID列表
        const receiptIds = this.aiUnmergedReceipts.map(receipt => receipt.id)
        const result = await mergeManagementApi.analyzeReceiptMerge(receiptIds)

        if (result.success) {
          this.aiReceiptSuggestions = result.data
          this.selectedAiReceiptGroups = []
          const groupCount = result.data && result.data.groups ? result.data.groups.length : 0
          this.$Message.success(`AI分析完成，建议了 ${groupCount} 个归并组`)
        } else {
          this.$Message.error(result.message || 'AI分析失败')
        }
      } catch (error) {
        console.error('AI银行回单分析失败:', error)
        this.$Message.error('AI分析操作失败')
      } finally {
        this.aiReceiptAnalyzing = false
      }
    },

    getReceiptInfo(receiptId) {
      const receipt = this.aiUnmergedReceipts.find(r => r.id === receiptId)
      if (!receipt) return ''
      return `${receipt.receiptNo || ''} ¥${this.formatAmount(receipt.amount)}`
    },

    toggleAiReceiptGroupSelection(index) {
      const selectedIndex = this.selectedAiReceiptGroups.indexOf(index)
      if (selectedIndex > -1) {
        this.selectedAiReceiptGroups.splice(selectedIndex, 1)
      } else {
        this.selectedAiReceiptGroups.push(index)
      }
    },

    selectAllAiReceiptGroups() {
      if (this.aiReceiptSuggestions && this.aiReceiptSuggestions.groups) {
        this.selectedAiReceiptGroups = this.aiReceiptSuggestions.groups.map((_, index) => index)
      }
    },

    clearAiReceiptGroupSelection() {
      this.selectedAiReceiptGroups = []
    },

    async executeSingleAiReceiptGroup(group, index) {
      try {
        const result = await mergeManagementApi.executeSingleReceiptAiMerge({
          receiptIds: group.receiptIds,
          groupName: group.groupName,
          reason: group.reason
        })

        if (result.success) {
          this.$Message.success('AI银行回单归并组创建成功！')
          await this.loadData()
          // 从建议中移除已执行的组
          this.aiReceiptSuggestions.groups.splice(index, 1)
          // 更新选中状态
          this.selectedAiReceiptGroups = this.selectedAiReceiptGroups
            .filter(i => i !== index)
            .map(i => i > index ? i - 1 : i)
        } else {
          this.$Message.error(result.message || '创建AI银行回单归并组失败')
        }
      } catch (error) {
        console.error('执行AI银行回单归并失败:', error)
        this.$Message.error('AI银行回单归并操作失败')
      }
    },

    async executeSelectedAiReceiptGroups() {
      if (this.selectedAiReceiptGroups.length === 0) {
        this.$Message.warning('请选择要执行的归并组')
        return
      }

      const selectedGroups = this.selectedAiReceiptGroups.map(index =>
        this.aiReceiptSuggestions.groups[index]
      )

      try {
        const result = await mergeManagementApi.executeSelectedReceiptAiMerge(
          selectedGroups.map(group => ({
            receiptIds: group.receiptIds,
            groupName: group.groupName,
            reason: group.reason
          }))
        )

        if (result.success) {
          const createdCount = result.data && result.data.createdCount ? result.data.createdCount : selectedGroups.length
          this.$Message.success(`批量创建成功，共创建了 ${createdCount} 个归并组`)
          await this.loadData()
          // 清空AI建议
          this.aiReceiptSuggestions = null
          this.selectedAiReceiptGroups = []
        } else {
          this.$Message.error(result.message || '批量创建失败')
        }
      } catch (error) {
        console.error('批量执行AI银行回单归并失败:', error)
        this.$Message.error('批量操作失败')
      }
    },

    // 归并组管理相关方法
    getStatusText(status) {
      const statusMap = {
        'ACTIVE': '活跃',
        'INACTIVE': '非活跃',
        'DISSOLVED': '已解散'
      }
      return statusMap[status] || status
    },

    async refreshDocumentGroups() {
      await this.loadDocumentGroups()
      this.$Message.success('票据归并组列表已刷新')
    },

    async refreshReceiptGroups() {
      await this.loadReceiptGroups()
      this.$Message.success('银行回单归并组列表已刷新')
    },

    viewDocumentGroup(group) {
      // 跳转到归并组详情页
      this.$router.push({
        name: 'MergeGroupDetail',
        params: {
          type: 'document',
          id: group.groupId
        },
        query: {
          from: 'merge-management'
        }
      })
    },

    viewReceiptGroup(group) {
      // 跳转到归并组详情页
      this.$router.push({
        name: 'MergeGroupDetail',
        params: {
          type: 'receipt',
          id: group.groupId
        },
        query: {
          from: 'merge-management'
        }
      })
    },

    closeGroupDetail() {
      this.groupDetailVisible = false
      this.currentGroupId = ''
      this.currentGroupType = 'document'
    },

    handleGroupUpdated() {
      // 归并组信息更新后，刷新列表
      this.loadDocumentGroups()
      this.loadReceiptGroups()
    },

    handleGroupDissolved() {
      // 归并组解散后，刷新所有数据
      console.log('归并组已解散，开始刷新数据...')
      this.loadData()
      // 额外刷新当前标签页的数据
      if (this.activeTab === 'documents') {
        this.loadDocuments()
      } else if (this.activeTab === 'receipts') {
        this.loadReceipts()
      }
    },

    async dissolveDocumentGroup(group) {
      const content = `确定要解散票据归并组 "${group.groupName}" 吗？解散后组内的票据将重新变为未归并状态。`

      this.$Confirm(content, '确认解散归并组').then(async () => {
        try {
          const result = await mergeManagementApi.dissolveDocumentGroup(group.groupId)
          if (result.success) {
            this.$Message.success('票据归并组已解散')
            await this.loadData()
          } else {
            this.$Message.error(result.message || '解散失败')
          }
        } catch (error) {
          console.error('解散票据归并组失败:', error)
          this.$Message.error('解散操作失败')
        }
      })
    },

    async dissolveReceiptGroup(group) {
      const content = `确定要解散银行回单归并组 "${group.groupName}" 吗？解散后组内的银行回单将重新变为未归并状态。`

      this.$Confirm(content, '确认解散归并组').then(async () => {
        try {
          const result = await mergeManagementApi.dissolveReceiptGroup(group.groupId)
          if (result.success) {
            this.$Message.success('银行回单归并组已解散')
            await this.loadData()
          } else {
            this.$Message.error(result.message || '解散失败')
          }
        } catch (error) {
          console.error('解散银行回单归并组失败:', error)
          this.$Message.error('解散操作失败')
        }
      })
    }
  }
}
</script>

<style scoped>
/* 基础样式已在外部CSS文件中定义 */

/* 增强的表格单元格样式 */
.cell-content {
  width: 100%;
  min-height: 20px;
}

.primary-text {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.secondary-text {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  line-height: 1.3;
}

.amount-text {
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
  font-family: 'Courier New', monospace;
}

/* 类型徽章样式 */
.type-tag.type-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.type-tag.type-income {
  background: linear-gradient(135deg, #00b894, #00a085);
  color: white;
}

.type-tag.type-expense {
  background: linear-gradient(135deg, #fd79a8, #e84393);
  color: white;
}

.type-tag.type-default {
  background: linear-gradient(135deg, #e0e0e0, #bdbdbd);
  color: #333;
}

/* 表格单元格内容对齐 */
.simple-table td {
  vertical-align: top;
  padding: 12px 8px;
}

.simple-table td .cell-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
</style>
